import { useDebouncedSearch, useInvites } from '@consigli/hooks';
import { Invite, InviteStatus, StatusVariant } from '@consigli/types';
import { ColumnDef } from '@tanstack/react-table';
import { useState, useMemo } from 'react';
import { BsThreeDots } from 'react-icons/bs';

import {
  ActionButton,
  Badge,
  DataTable,
  DEFAULT_PAGE_SIZE,
  Pagination,
  Search,
  Select,
  ObjectDetailView,
} from '@/components';
import { formatTimestamp } from '@/utils/timestamp-converting';

const ALL_STATUS = 'ALL';

const statusOptions = [
  { label: 'All', value: ALL_STATUS },
  { label: 'Pending', value: InviteStatus.PENDING },
  { label: 'Accepted', value: InviteStatus.ACCEPTED },
  { label: 'Rejected', value: InviteStatus.REJECTED },
];

const statusVariantMap: Record<InviteStatus, StatusVariant> = {
  [InviteStatus.PENDING]: StatusVariant.WARNING,
  [InviteStatus.ACCEPTED]: StatusVariant.SUCCESS,
  [InviteStatus.REJECTED]: StatusVariant.ERROR,
};

export const InvitesTable = () => {
  const pageSize = DEFAULT_PAGE_SIZE;
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedSearch(searchTerm, 500);
  const [selectedStatus, setSelectedStatus] = useState<InviteStatus | undefined>(undefined);
  const [selectedInvite, setSelectedInvite] = useState<Invite | null>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);

  const { invites, paginatedCount, isFetching } = useInvites({
    page,
    pageSize,
    search: debouncedSearchTerm || undefined,
    status: selectedStatus,
  });

  const handleViewDetails = (invite: Invite) => {
    setSelectedInvite(invite);
    setIsDetailViewOpen(true);
  };

  const columns: ColumnDef<Invite>[] = useMemo(
    () => [
      {
        header: 'Recipient',
        accessorKey: 'recipient',
      },
      {
        header: 'Invited By',
        accessorKey: 'invitedByEmail',
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => {
          const status = row.original.status;
          return <Badge text={status} variant={statusVariantMap[status]} />;
        },
      },
      {
        header: 'Organization',
        accessorKey: 'organization',
        cell: ({ row }) => {
          const org = row.original.organization;
          return org ? `${org.role}` : 'Project Access Only';
        },
      },
      {
        header: 'Projects',
        accessorKey: 'projects',
        cell: ({ row }) => {
          const projects = row.original.projects;
          return projects?.length ?? 0;
        },
      },
      {
        header: 'Expires',
        accessorKey: 'expiresAt',
        cell: ({ row }) => formatTimestamp(row.original.expiresAt, false, false),
      },
      {
        header: '',
        id: 'actions',
        cell: ({ row }) => (
          <ActionButton
            icon={<BsThreeDots />}
            actions={[
              {
                label: 'View Details',
                onClick: () => handleViewDetails(row.original),
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  const selectedStatusOption = useMemo(
    () =>
      statusOptions.find((opt) => opt.value === (selectedStatus ?? ALL_STATUS)) || statusOptions[0],
    [selectedStatus],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <Search
          className="w-[250px]"
          text={searchTerm}
          placeholder="Search by email"
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
        />
        <Select
          options={statusOptions}
          value={selectedStatusOption}
          onValueChange={(option) => {
            setSelectedStatus(
              option?.value && option.value !== ALL_STATUS
                ? (option.value as InviteStatus)
                : undefined,
            );
            setPage(1);
          }}
          placeholder="Filter by status"
          getOptionLabel={(opt) => opt?.label ?? ''}
          getOptionValue={(opt) => opt?.value ?? ''}
          className="w-[180px]"
        />
      </div>
      <DataTable height="60vh" columns={columns} data={invites} isLoading={isFetching} scrollable />
      <Pagination
        onPageChange={setPage}
        pageSize={pageSize}
        currentPage={page}
        totalCount={paginatedCount}
      />

      {selectedInvite && (
        <ObjectDetailView
          object={selectedInvite}
          objectType="Invite"
          summary={`Invite to ${selectedInvite.recipient}`}
          isOpen={isDetailViewOpen}
          onOpenChange={(open: boolean) => {
            setIsDetailViewOpen(open);
            if (!open) setSelectedInvite(null);
          }}
        />
      )}
    </div>
  );
};
